import React, { useEffect, useMemo, useState } from "react";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ThemeColor } from "../../component/theme/color";
import OutlinedInputField from "../../component/inputfiled/OutlinedInput";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomButton from "../../component/button/button";
import OutlineCustomButton from "../../component/button/outlineButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  CreateCountryService,
  UpdateCountryService,
} from "../../Redux/service/SettingService";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import { GetCountryAction } from "../../Redux/action/SettingAction";

const validationSchema = Yup.object({
  country: Yup.string().trim().required("Country name is required"),
  states: Yup.array()
    .of(Yup.string().trim().required("State name cannot be empty"))
    .test("no-duplicates", "The same state is listed twice", (states = []) => {
      const cleaned = states
        .map((state) => String(state || "").trim().toLowerCase())
        .filter(Boolean);
      return new Set(cleaned).size === cleaned.length;
    }),
});

const CountryFormPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The country name is the identifier - the list endpoint does not expose _id,
  // and delete works by name too.
  const { country: countryParam } = useParams();
  const isEdit = Boolean(countryParam);
  const countryName = countryParam ? decodeURIComponent(countryParam) : "";

  const [formLoading, setFormLoading] = useState(false);

  const countryListState = useSelector(
    (state) => state.setting?.countryCount?.data?.data
  );

  useEffect(() => {
    // Editing needs the stored states; creating does not need this at all, but
    // the list is small and it keeps the duplicate-country check honest.
    dispatch(GetCountryAction({ page: 1, limit: 0 }));
  }, [dispatch]);

  const existing = useMemo(
    () =>
      (countryListState || []).find(
        (entry) =>
          String(entry.country || "").toLowerCase() === countryName.toLowerCase()
      ),
    [countryListState, countryName]
  );

  const initialValues = useMemo(
    () => ({
      country: isEdit ? existing?.country || countryName : "",
      // Always leave one empty row to type into.
      states: existing?.states?.length ? [...existing.states] : [""],
    }),
    [isEdit, existing, countryName]
  );

  if (isEdit && !existing && !countryListState) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <CircularProgress color="inherit" />
      </div>
    );
  }

  const handleSubmit = async (values, { resetForm }) => {
    const states = [
      ...new Set(
        values.states.map((state) => String(state || "").trim()).filter(Boolean)
      ),
    ];

    setFormLoading(true);
    try {
      const res = isEdit
        ? await UpdateCountryService(values.country.trim(), states)
        : await CreateCountryService({ country: values.country.trim(), states });

      if (res.status === 200 || res.status === 201) {
        dispatch(showSnackbar(res.data?.message || "Saved", "success"));
        dispatch(GetCountryAction({ page: 1, limit: 50 }));
        resetForm();
        navigate("/CAE/CountryList");
      }
    } catch (error) {
      dispatch(
        showSnackbar(
          error?.response?.data?.message || "Could not save the country",
          "error"
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center">
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          fontFamily={FontFamily.Poppins}
          fontWeight={fontWeight.W600}
          fontSize={fontSize.h2.small}
        >
          Settings
        </Typography>
        <Typography
          className="px-2 pt-1"
          fontFamily={FontFamily.Poppins}
          fontSize={fontSize.h4.medium}
          fontWeight={fontWeight.W300}
          color={ThemeColor.GREY}
        >
          {isEdit ? "Edit" : "Add"} Country
        </Typography>
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  width: { xs: "100%", md: "80%", lg: "70%" },
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px",
                  p: { xs: 2.5, md: 3.5 },
                }}
              >
                <Typography
                  align="center"
                  fontFamily={FontFamily.Poppins}
                  fontWeight={fontWeight.W600}
                  fontSize={fontSize.h3.medium}
                  color={ThemeColor.PURPLE}
                >
                  {isEdit ? "Edit" : "Add"} Country
                </Typography>

                <FormGroup sx={{ mt: 3 }}>
                  <Typography
                    className="mb-2"
                    sx={{
                      fontWeight: 520,
                      fontSize: "0.9rem",
                      letterSpacing: "0.00938em",
                      fontFamily: "Lato !important",
                    }}
                  >
                    Country Name *
                  </Typography>
                  <OutlinedInputField
                    value={values.country}
                    onChange={handleChange}
                    name="country"
                    // Renaming is deliberately not offered: universities,
                    // organizations and students all store the country as a
                    // plain string, so a rename here would orphan them.
                    disable={isEdit}
                    placeholder="Enter the country name...."
                    fullWidth={true}
                    helperText={
                      <Typography
                        align="left"
                        color={ThemeColor.RED}
                        fontSize={fontSize.h4.medium}
                        fontFamily={FontFamily.Lato}
                        fontWeight={fontWeight.W500}
                      >
                        {touched.country && errors.country}
                      </Typography>
                    }
                  />
                  {isEdit && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mt: -1,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 15, color: ThemeColor.GREY }}
                      />
                      <Typography
                        sx={{ fontSize: 12, color: ThemeColor.GREY }}
                      >
                        The country name cannot be changed - universities and
                        organizations refer to it by name.
                      </Typography>
                    </Box>
                  )}
                </FormGroup>

                <Divider sx={{ my: 3 }}>
                  <Chip
                    size="small"
                    label={`States${
                      values.states.filter((state) => String(state || "").trim())
                        .length
                        ? ` · ${
                            values.states.filter((state) =>
                              String(state || "").trim()
                            ).length
                          }`
                        : ""
                    }`}
                  />
                </Divider>

                <FieldArray name="states">
                  {({ push, remove }) => (
                    <>
                      <Grid container spacing={2}>
                        {values.states.map((_, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box
                              sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <OutlinedInputField
                                  value={values.states[index]}
                                  name={`states[${index}]`}
                                  onChange={handleChange}
                                  placeholder={`State ${index + 1}`}
                                  fullWidth={true}
                                  helperText={
                                    <Typography
                                      align="left"
                                      color={ThemeColor.RED}
                                      fontSize={fontSize.h4.medium}
                                      fontFamily={FontFamily.Lato}
                                      fontWeight={fontWeight.W500}
                                    >
                                      {touched.states?.[index] &&
                                        errors.states?.[index]}
                                    </Typography>
                                  }
                                />
                              </Box>
                              <Tooltip title="Remove this state">
                                <IconButton
                                  size="small"
                                  sx={{ mt: 0.75, color: ThemeColor.RED }}
                                  // Never leave the list with no row at all -
                                  // clear the last one instead of removing it.
                                  onClick={() =>
                                    values.states.length === 1
                                      ? setFieldValue("states[0]", "")
                                      : remove(index)
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {typeof errors.states === "string" && (
                        <Typography
                          sx={{ mt: 1, color: ThemeColor.RED, fontSize: 13 }}
                        >
                          {errors.states}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        <IconButton
                          className="form-icon"
                          sx={{
                            background: ThemeColor.BLUE,
                            color: ThemeColor.WHITE,
                            "&:hover": { background: ThemeColor.NAVYBLUE },
                          }}
                          onClick={() => push("")}
                        >
                          <AddIcon />
                        </IconButton>
                        <Typography
                          fontSize={fontSize.h3.small}
                          fontFamily={FontFamily.Lato}
                          color={ThemeColor.BLACK}
                          fontWeight={fontWeight.W600}
                        >
                          Add another state
                        </Typography>
                      </Box>
                    </>
                  )}
                </FieldArray>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1.5,
                    mt: 4,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <CustomButton
                    variant="contained"
                    type="submit"
                    backgroundColor={ThemeColor.PURPLE}
                    color={ThemeColor.WHITE}
                    loading={formLoading}
                  >
                    {isEdit ? "Save Changes" : "Save Country"}
                  </CustomButton>
                  <OutlineCustomButton
                    variant="outlined"
                    type="button"
                    onClick={() => navigate("/CAE/CountryList")}
                  >
                    Cancel
                  </OutlineCustomButton>
                </Box>
              </Paper>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

function AddCountry() {
  return (
    <div>
      <ResponsiveDrawer maincontent={<CountryFormPage />} />
    </div>
  );
}

export default AddCountry;
